import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { SectionHeading } from './SectionHeading';

const stateLeaders = [
  {
    name: 'Sri Nara Chandrababu Naidu',
    role: "Hon'ble Chief Minister",
    sub: 'Andhra Pradesh',
    image: 'https://cdma.ap.gov.in/assets/profile-pics/chandrababu-naidu.png',
    fallback: '/images/leadership/chandrababu-naidu.png',
  },
  {
    name: 'Sri Ponguru Narayana',
    role: "Hon'ble Minister",
    sub: 'MA&UD, Andhra Pradesh',
    image: 'https://cdma.ap.gov.in/assets/profile-pics/ponguru-narayana.png',
    fallback: '/images/leadership/ponguru-narayana.png',
  },
  {
    name: 'Sri S. Suresh Kumar, IAS',
    role: 'Principal Secretary',
    sub: 'MA&UD Department',
    image: '/images/leadership/suresh-kumar.png',
    fallback: '/images/leadership/suresh-kumar.png',
  },
  {
    name: 'Smt. Ravuri Jyothi Vidyullatha',
    role: 'Director',
    sub: 'Town & Country Planning',
    image: '/images/leadership/jyothi-vidyullatha.png',
    fallback: '/images/leadership/jyothi-vidyullatha.png',
  },
];

/* Two layered city-skyline silhouettes (viewBox 1200×160, anchored to the
   bottom) used as a decorative backdrop — a subtle nod to the Authority's
   urban-development brief. Rendered at low opacity so the leader cards stay
   the focus. {x, w, h} = building x-offset, width and height in viewBox units. */
const SKYLINE_BACK = [
  { x: 0, w: 60, h: 70 }, { x: 66, w: 40, h: 50 }, { x: 110, w: 54, h: 96 }, { x: 168, w: 44, h: 60 },
  { x: 216, w: 70, h: 112 }, { x: 290, w: 38, h: 56 }, { x: 332, w: 58, h: 86 }, { x: 394, w: 46, h: 66 },
  { x: 444, w: 64, h: 102 }, { x: 512, w: 40, h: 58 }, { x: 556, w: 56, h: 80 }, { x: 616, w: 48, h: 94 },
  { x: 668, w: 70, h: 122 }, { x: 742, w: 42, h: 64 }, { x: 788, w: 58, h: 90 }, { x: 850, w: 46, h: 68 },
  { x: 900, w: 66, h: 106 }, { x: 970, w: 40, h: 58 }, { x: 1014, w: 56, h: 84 }, { x: 1074, w: 48, h: 98 },
  { x: 1126, w: 80, h: 72 },
];
const SKYLINE_FRONT = [
  { x: -12, w: 84, h: 46 }, { x: 76, w: 60, h: 64 }, { x: 140, w: 50, h: 40 }, { x: 194, w: 74, h: 74 },
  { x: 272, w: 54, h: 48 }, { x: 330, w: 64, h: 68 }, { x: 398, w: 48, h: 44 }, { x: 450, w: 80, h: 80 },
  { x: 534, w: 52, h: 50 }, { x: 590, w: 66, h: 60 }, { x: 660, w: 50, h: 86 }, { x: 714, w: 78, h: 54 },
  { x: 796, w: 56, h: 72 }, { x: 856, w: 64, h: 46 }, { x: 924, w: 50, h: 66 }, { x: 978, w: 80, h: 50 },
  { x: 1062, w: 58, h: 76 }, { x: 1124, w: 92, h: 60 },
];

export const DepartmentSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.15 });
  const leaders = stateLeaders;

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={`scroll-reveal portal-section portal-surface-alt portal-rule relative overflow-hidden ${isRevealed ? 'revealed' : ''}`}
    >
      {/* Decorative backdrop — a soft "dawn over the city": an accent glow, a
          faint dot grid and two layered urban-skyline silhouettes. Purely
          cosmetic; nods to the Authority's urban-development brief.
          Previously this carried three separate coloured blooms (orange, green
          and gold) which fought both each other and the leader portraits; it
          is now a single accent wash so the faces stay the subject. */}
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        {/* Accent glow, top-centre */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(62% 58% at 50% -10%, color-mix(in srgb, var(--portal-accent) 16%, transparent), transparent 64%)',
          }}
        />
        <div
          className="absolute -top-28 -left-24 w-[440px] h-[440px] rounded-full blur-[130px] opacity-[0.16]"
          style={{ background: 'var(--portal-accent)' }}
        />
        <div
          className="absolute -bottom-32 -right-20 w-[460px] h-[460px] rounded-full blur-[140px] opacity-[0.10]"
          style={{ background: 'var(--portal-accent-dark)' }}
        />
        {/* Faint dot grid */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="leaders-dots" width="26" height="26" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.3" fill="currentColor" />
            </pattern>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="url(#leaders-dots)"
            opacity="0.07"
            style={{ color: 'var(--portal-accent)' }}
          />
        </svg>
        {/* Layered city skyline, anchored to the bottom edge */}
        <svg
          className="absolute bottom-0 left-0 w-full h-[170px]"
          viewBox="0 0 1200 160"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ color: 'var(--portal-accent)' }}
        >
          <g fill="currentColor" opacity="0.13">
            {SKYLINE_BACK.map((b, i) => (
              <rect key={`bk-${i}`} x={b.x} y={160 - b.h} width={b.w} height={b.h} rx="2" />
            ))}
          </g>
          <g fill="currentColor" opacity="0.20" style={{ color: 'var(--portal-accent-dark)' }}>
            {SKYLINE_FRONT.map((b, i) => (
              <rect key={`fr-${i}`} x={b.x} y={160 - b.h} width={b.w} height={b.h} rx="2" />
            ))}
          </g>
        </svg>
      </div>

      <div className="reveal-content portal-container relative z-10">
        <SectionHeading
          eyebrow="Leadership"
          title={t('leadership.title')}
          subtitle="The elected representatives and senior officers directing town and country planning across Andhra Pradesh."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-7">
          {leaders.map((leader, i) => (
            <div
              key={i}
              className="stagger-child group relative flex flex-col items-center rounded-3xl bg-white/85 dark:bg-white/[0.05] backdrop-blur-md border border-white/70 dark:border-white/10 shadow-[0_10px_34px_-14px_rgba(0,0,0,0.28)] overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_28px_56px_-20px_rgba(0,0,0,0.4)]"
            >
              {/* Cover band in the portal accent */}
              <div
                className="relative w-full h-[84px] overflow-hidden"
                style={{
                  background:
                    'linear-gradient(105deg, var(--portal-accent-dark), var(--portal-accent) 55%, var(--portal-accent-light))',
                }}
              >
                <span className="absolute -top-7 -right-7 w-24 h-24 rounded-full bg-white/15 pointer-events-none" />
                <span className="absolute -bottom-9 left-5 w-20 h-20 rounded-full bg-white/10 pointer-events-none" />
                <span className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-white/25 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </div>

              {/* Photo overlapping the band, inside a clean accent ring */}
              <div
                className="relative -mt-[56px] w-[112px] h-[112px] rounded-full p-[3px] shadow-[0_10px_26px_-8px_rgba(0,0,0,0.35)] transition-transform duration-500 group-hover:scale-[1.06]"
                style={{ background: 'linear-gradient(135deg, var(--portal-accent), var(--portal-accent-light))' }}
              >
                <div className="w-full h-full rounded-full overflow-hidden border-[4px] border-white bg-white">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    loading="eager"
                    onError={(e) => { const img = e.target as HTMLImageElement; if (img.src !== leader.fallback) img.src = leader.fallback; }}
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex flex-col items-center text-center px-5 pt-4 pb-6">
                <h3 className="text-[15.5px] font-extrabold text-[#161A22] dark:text-white leading-snug">
                  {leader.name}
                </h3>
                <span
                  className="mt-2.5 inline-flex items-center rounded-full px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.08em]"
                  style={{ background: 'var(--portal-accent-soft)', color: 'var(--portal-accent-dark)' }}
                >
                  {leader.role}
                </span>
                {leader.sub && (
                  <p className="mt-2 text-[12px] font-medium text-[#6B7280] dark:text-white/55 leading-snug">
                    {leader.sub}
                  </p>
                )}
              </div>

              {/* Bottom accent line on hover */}
              <div
                className="absolute bottom-0 left-0 right-0 h-[3px] origin-center scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                style={{ background: 'linear-gradient(90deg, var(--portal-accent), var(--portal-accent-light))' }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
